import { useState } from 'react';
import { AppBar, Toolbar, Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, IconButton, Avatar, Menu, MenuItem, Divider } from '@mui/material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../contexts/AuthContext';
import { NAV_BG } from '../theme';
import BrandLogo from '../components/BrandLogo';

const DRAWER = 240;

const NAV = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { label: 'Tenants', icon: <BusinessIcon />, path: '/tenants' },
  { label: 'Tickets', icon: <ConfirmationNumberIcon />, path: '/tickets' },
  { label: 'Users', icon: <PeopleIcon />, path: '/users' },
  { label: 'Roles', icon: <SecurityIcon />, path: '/roles' },
  { label: 'Reports', icon: <AssessmentIcon />, path: '/reports' }
];

export default function DashboardLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, logout } = useAuth();
  const [menu, setMenu] = useState(null);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <BrandLogo brand="leano" variant="light" height={28} />
            <Box sx={{ height: 22, width: '1px', bgcolor: 'rgba(255,255,255,0.25)' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, opacity: 0.92 }}>
              {user?.resellerName || 'Reseller Portal'}
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={(e) => setMenu(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
              {(user?.firstName || user?.email || '?').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
            <MenuItem disabled>{user?.email}</MenuItem>
            <Divider />
            <MenuItem onClick={() => { nav('/change-password'); setMenu(null); }}>Change password</MenuItem>
            <MenuItem onClick={logout}><ArrowBackIcon fontSize="small" sx={{ mr: 1 }} />Back to Main Portal</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" sx={{
        width: DRAWER, flexShrink: 0,
        [`& .MuiDrawer-paper`]: { width: DRAWER, bgcolor: NAV_BG, color: '#fff', borderRight: 0, display: 'flex', flexDirection: 'column' }
      }}>
        <Toolbar sx={{ px: 2 }}>
          {user?.resellerLogoDataUrl
            ? <Box component="img" src={user.resellerLogoDataUrl} alt="" sx={{ maxHeight: 40 }} />
            : <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.7 }}>Reseller</Typography>}
        </Toolbar>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <List sx={{ flexGrow: 1 }}>
          {NAV.map(n => (
            <ListItemButton key={n.path} selected={loc.pathname === n.path} onClick={() => nav(n.path)}
              sx={{
                color: '#fff',
                '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.12)' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' }
              }}>
              <ListItemIcon sx={{ color: '#fff' }}>{n.icon}</ListItemIcon>
              <ListItemText primary={n.label} />
            </ListItemButton>
          ))}
        </List>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.55, letterSpacing: 1.2, textTransform: 'uppercase', mb: 0.75, fontSize: 10 }}>
            Powered by
          </Typography>
          <BrandLogo brand="izzat" variant="light" height={20} sx={{ mx: 'auto', opacity: 0.9 }} />
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
